import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DeploymentConfigInfo, DeploymentMode } from './appDeploymentInfoTypes';
import { Backend, createBackend } from '../auth/auth';
import { ConfigInterface } from '../../app-config';

export type AppConfigSliceState = {
    /** Current configuration regarding this app's deployment, ie. configuration that's specific
     * to this instance of this app that's running on this particular web server. Can contain
     * e.g. configuration specific to current region (eu, uk, us, etc).
     */
    appDeploymentInfo: DeploymentConfigInfo | undefined;

    /** Current backend, if set. NOTE: not used in Cockpit deployment mode. */
    backend: Backend | undefined,

    /** Current major app configuration, usually based on deployment mode (cloud or local). */
    appConfig: ConfigInterface | undefined,
};

export const initialState: AppConfigSliceState = {
    appDeploymentInfo: undefined,
    backend: undefined,
    appConfig: undefined,
};

/** Redux slice for handling app configuration */
const appConfigSlice = createSlice({
    name: 'appConfig',
    initialState,
    reducers: {
        /** Sets app deployment info to supplied value. This is generally read from the config.json file on the hosting web
         * server's root.
         * @param action payload: the DeploymentConfigInfo object to use as app deployment info.
         */
        appDeploymentInfoSet(state, action: PayloadAction<DeploymentConfigInfo>) {
            const appDeploymentInfo = action.payload;
            state.appDeploymentInfo = appDeploymentInfo;

            // set cloud backend if relevant
            if (appDeploymentInfo.deploymentMode === DeploymentMode.Cloud && appDeploymentInfo.backendUrl !== undefined && appDeploymentInfo.cloudBackendTier !== undefined) {
                const backend = createBackend(appDeploymentInfo.backendName || 'Backend', appDeploymentInfo.backendUrl, appDeploymentInfo.cloudBackendTier);
                state.backend = backend;
            }
            //  set local backend if relevant
            else if (appDeploymentInfo.deploymentMode === DeploymentMode.Local) {
                // Use browser's current root address if localUseBrowserUrlAsBackendUrl is true
                const backendUrl = appDeploymentInfo.localUseBrowserUrlAsBackendUrl
                    ? window.location.origin
                    : appDeploymentInfo.backendUrl;

                if (backendUrl !== undefined) {
                    const backend = createBackend(appDeploymentInfo.backendName || 'Backend', backendUrl, undefined);
                    state.backend = backend;
                } else {
                    throw new Error('Could not configure backend settings -- please check your config.json configuration.');
                }
            } else if (appDeploymentInfo.deploymentMode !== DeploymentMode.Cockpit) {
                throw new Error('Could not configure backend settings -- please check your config.json configuration.');
            }
        },

        /** Sets main app configuration, generally based on current deployment mode (cloud or local environment).
         * @param action payload: the ConfigInterface object to use as app config.
         */
        appConfigSet(state, action: PayloadAction<ConfigInterface>) {
            const appConfig = action.payload;
            state.appConfig = appConfig;
        },
    },
    selectors: {
        /** Returns app deployment info (i.e. config.json) */
        selectAppDeploymentInfo: (state) => state.appDeploymentInfo,

        /** Returns current cloud backend. NOTE: not used in Cockpit deployment mode. */
        selectBackend: (state) => state.backend,

        /** Returns current main app configuration */
        selectAppConfig: (state) => state.appConfig,

        /** Returns true if the page layout should be rendered inside a containing frame (i.e. cockpit), false if not, and undefined if unknown (i.e. if config is not yet loaded). */
        selectIsLayoutInsideFrame: (state) => state.appConfig?.isInsideFrame,
    }
});

export const { appDeploymentInfoSet, appConfigSet } = appConfigSlice.actions;

// uncomment if these are needed locally
// const localSelectors = appConfigSlice.getSelectors();

export const { getInitialState, selectors: appConfigSelectors } = appConfigSlice;

export default appConfigSlice.reducer;
